import React, { useContext, useEffect, useState } from 'react';
import { MedicalGroup, PlanService, NetworkStatus, SelectedNetwork } from '../../utils/types';
import { Autocomplete, TextField, Link, Button } from '@mui/material';
import { SearchContainer, MobileBreakPoint, notSureStyles, SectionHeader } from '../../theme';
import { isMobile } from 'react-device-detect';
import NoMatch from './NoMatch';
import { DataContext } from '../../containers/InsuranceChecker';
import { Container, Title } from './styles';
import NotSureMedicalGroupModal from '../Modals/NotSureMedicalGroupModal';
import { EventAction, trackMixpanelEvent } from '../../mixpanelAnalytics';

type AutocompleteOption = string;

type Props = {
    handleMedicalGroupSelect: (group: MedicalGroup) => void;
    service: PlanService | undefined;
    network: SelectedNetwork | undefined;
    handleStartOver: () => void;
}

const MedicalGroupsContainer = ({ handleMedicalGroupSelect, handleStartOver, service, network }:Props): JSX.Element => {
    const [ value, setValue ] = useState('');
    const [ lastTrackedValue, setLastTrackedValue ] = useState('');
    const { groups } = useContext(DataContext);
    const [ options, setOptions ] = useState<AutocompleteOption[]>([]);
    const [ focus, setFocus ] = useState(isMobile || window.innerWidth <= MobileBreakPoint);
    const [ openModal, setOpenModal ] = useState(false);
    const [ noMatch, setNoMatch ] = useState(false);

    const getService = (group:MedicalGroup, serviceName: string):PlanService => {
        return group.services.filter(s => s.name === serviceName)[0];
    };

    useEffect(() => {
        if (service) {
            const options = groups.filter(g => getService(g, service.name).serviceStatus !== NetworkStatus.NotAvailable).map(g => g.name);
            setOptions(options);
        }
    }, [ groups ]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (value && lastTrackedValue !== value) {
                setLastTrackedValue(value);
                trackMixpanelEvent(EventAction.QUERIED_MEDICAL_GROUP, { value });
            }
        }, 1000);
        return () => clearTimeout(timeout);
    }, [ value ]);

    const handleInputChange = (event:React.SyntheticEvent, value:string):void => {
        setValue(value);
        const selected = groups.filter(g => g.name === value);
        if (selected.length) {
            handleMedicalGroupSelect(selected[0]);
        }
    };

    return (
        <SearchContainer>
            <Container style={{ height: focus ? '600px' : 'auto' }}>
                <Title>
                    <SectionHeader>Enter your Medical Group/ IPA</SectionHeader>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={ () => (setOpenModal(!openModal)) } variant='text' sx={notSureStyles} style={{ padding: '0px', whiteSpace: 'nowrap' }}>Not Sure?</Button>
                    </div>
                </Title>
                <Autocomplete
                    disablePortal
                    id='medical-group'
                    options={options}
                    sx={{
                        borderRadius: focus ? '40px 40px 0px 0px' : '40px',
                        boxShadow: 'none',
                    }}
                    onInputChange={handleInputChange}
                    onFocus={() => { setFocus(true); }}
                    onBlur={() => { setFocus(false); }}
                    open={focus}
                    autoComplete={true}
                    autoHighlight={true}
                    renderInput={(params) => <TextField {...params} placeholder='Medical Group/ IPA'/>}
                    componentsProps={{ popper: { placement: 'bottom-start', sx: { height: '50%' } } }}
                    ListboxProps={{ sx: { maxHeight: isMobile ? '450px' : '40vh' } }}
                    noOptionsText={<Link onClick={() => { setNoMatch(true); setFocus(false); }}>I don't see my medical group</Link>}
                />
                <NoMatch show={noMatch} service={service} network={network} handleStartOver={handleStartOver}/>
            </Container>
            <NotSureMedicalGroupModal open={openModal} handleClose={() => { setOpenModal(false); }}/>
        </SearchContainer>
    );
};

export default MedicalGroupsContainer;
